
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Application } from "@/types/application";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Settings, UserPlus, Tags, Eye } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CustomerTag from "@/components/CustomerTag";
import { mockCustomers } from "@/utils/customerGroups";
import { ConfigureApplicationDialog } from "./ConfigureApplicationDialog";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { SortableHeader } from "@/components/ui/table-cells";

interface ApplicationListProps {
  applications: Application[];
  onUpdateApplication?: (app: Application) => void;
  onRemoveApplication?: (appId: string) => void;
}

export const ApplicationList = ({ 
  applications,
  onUpdateApplication,
  onRemoveApplication
}: ApplicationListProps) => {
  const navigate = useNavigate();
  const [configAppId, setConfigAppId] = useState<string | null>(null);
  const [tagAssignmentAppId, setTagAssignmentAppId] = useState<string | null>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  
  const handleConfigureClick = (appId: string) => {
    setConfigAppId(appId);
  };
  
  const handleConfigDialogClose = () => {
    setConfigAppId(null);
  };
  
  const getConfiguredApp = () => {
    return applications.find(app => app.id === configAppId);
  };
  
  const handleSaveConfig = (updatedApp: Application) => {
    if (onUpdateApplication) {
      onUpdateApplication(updatedApp);
    }
    setConfigAppId(null);
  };

  const getTagAssignmentApp = () => {
    return applications.find(app => app.id === tagAssignmentAppId);
  };

  const columns: ColumnDef<Application>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Application</SortableHeader>,
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate(`/applications/${app.id}`)}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden">
              <img src={app.icon} alt={app.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-medium">{app.name}</div>
              <div className="text-xs text-muted-foreground">{app.bundleId}</div>
            </div>
          </div>
        );
      },
      size: 300,
    },
    {
      accessorKey: "version",
      header: ({ column }) => <SortableHeader column={column}>Version</SortableHeader>,
      cell: ({ row }) => <div>{row.getValue("version")}</div>,
      size: 120,
    },
    {
      accessorKey: "size",
      header: ({ column }) => <SortableHeader column={column}>Size</SortableHeader>,
      cell: ({ row }) => <div>{row.getValue("size")}</div>,
      size: 100,
    },
    {
      accessorKey: "platform",
      header: ({ column }) => <SortableHeader column={column}>Platform</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant="outline">{row.getValue("platform")}</Badge>
      ),
      size: 120,
    },
    {
      accessorKey: "customers",
      header: "Assigned to",
      cell: ({ row }) => {
        const app = row.original;
        const customerNames = app.customers
          .map(id => mockCustomers.find(c => c.id === id)?.name || "")
          .filter(name => name !== "");
        
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {customerNames.length > 0 ? (
              customerNames.map((name, i) => (
                <CustomerTag key={i} customer={name} />
              ))
            ) : (
              <span className="text-xs italic text-muted-foreground">No assignments</span>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div className="flex items-center justify-end">
            <ActionsDropdown
              actions={[
                {
                  label: "Publish",
                  icon: ArrowRight,
                  onClick: () => {
                    // TODO: Implement publish functionality
                  },
                },
                {
                  label: "View Details",
                  icon: Eye,
                  onClick: () => navigate(`/applications/${app.id}`),
                },
                {
                  label: "Assign to Customers",
                  icon: UserPlus,
                  onClick: () => {
                    if (onUpdateApplication) {
                      onUpdateApplication(app);
                    }
                  },
                },
                {
                  label: "Assign Tags",
                  icon: Tags,
                  onClick: () => setTagAssignmentAppId(app.id),
                },
                {
                  label: "Configure",
                  icon: Settings,
                  onClick: () => handleConfigureClick(app.id),
                },
              ]}
            />
          </div>
        );
      },
      size: 200,
    },
  ];

  return (
    <div className="w-full min-w-0">
      <DataTable
        data={applications}
        columns={columns}
        enableSorting={true}
        enableColumnResizing={true}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No applications found"
      />
      
      {configAppId && getConfiguredApp() && (
        <ConfigureApplicationDialog
          application={getConfiguredApp()!}
          open={!!configAppId}
          onOpenChange={handleConfigDialogClose}
          onSave={handleSaveConfig}
        />
      )}

      {tagAssignmentAppId && getTagAssignmentApp() && (
        <TagAssignmentDialog
          open={!!tagAssignmentAppId}
          onOpenChange={(open) => !open && setTagAssignmentAppId(null)}
          resourceId={getTagAssignmentApp()!.id}
          resourceType="application"
          resourceName={getTagAssignmentApp()!.name}
          onAssignmentComplete={() => {
            setTagAssignmentAppId(null);
          }}
        />
      )}
    </div>
  );
};
